import axios, { AxiosRequestConfig, AxiosResponse, HttpStatusCode } from 'axios';
import { ApiHost } from '../config/Config';
import { TokenStore } from '../data/stores/TokenStore';
import { NoticeStoreInstance, NoticeType } from '../data/stores/NoticeStore';
import { NoPermission } from '../components/notices/NoPermission';
import { ServerError } from '../components/notices/ServerError';

type ApiHeaders = {
  Authorization?: string;
};

export enum ResourceStatus {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
  DELETED = 'deleted',
}

export type PageWithTotal<T> = {
  content: T[];
  transactionTotal: number;
  totalPages: number;
  totalElements: number;
};

export type ApiError = {
  message: string;
};

export class InternalServerError extends Error {}
export class PermissionsError extends Error {
  statusCode: HttpStatusCode;
  constructor(statusCode: HttpStatusCode) {
    super();
    this.statusCode = statusCode
  }
}

export class API {
  static axios() {
    const config = {
      baseURL: ApiHost,
      timeout: 300000,
      headers: this.headers(),
    };
    return axios.create(config);
  }

  static headers(): ApiHeaders {
    const headers: ApiHeaders = {};

    // Set the auth header if we have a token
    if (TokenStore.instance().hasToken) {
      headers.Authorization = `Bearer ${TokenStore.instance().token}`;
    }

    return headers;
  }

  static handleResponse(error: any) {
    if (error.response) {
      if (error.response.status === 401) {
        TokenStore.instance().reset();
        window.location = '/login' as any;
      }

      if (error.response.status >= 500) {
        NoticeStoreInstance.setNoticeType(ServerError, NoticeType.ERROR);
        throw new InternalServerError();
      }

      if (error.response.status === 403) {
        NoticeStoreInstance.setNoticeType(NoPermission, NoticeType.ERROR);
        throw new PermissionsError(error.response.status);
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw error;
  }

  static request(
    method: string,
    url: string,
    body: object,
    config: AxiosRequestConfig
  ) {
    // Ideally we'd use axios.request() here, but mock axios doesn't support that
    // and all our tests break. See https://github.com/knee-cola/jest-mock-axios/issues/18
    // .request(Object.assign({ url, method }, config))

    const axiosInstance = this.axios();

    // Unavailable in tests (at the moment)
    if (axiosInstance.interceptors) {
      // Add a global interceptor for error handling
      axiosInstance.interceptors.response.use(
        response => response,
        API.handleResponse
      );
    }

    if (body) {
      // @ts-ignore
      return axiosInstance[method](url, body, config);
    }
    // @ts-ignore
    return axiosInstance[method](url, config);
  }

  static delete = (url: string, config = {}): Promise<AxiosResponse> =>
    API.request('delete', url, null, config);
  static get = (url: string, config = {}): Promise<AxiosResponse> =>
    API.request('get', url, null, config);
  static post = (
    url: string,
    body: object,
    config = {}
  ): Promise<AxiosResponse> => API.request('post', url, body, config);
  static patch = (
    url: string,
    body: object,
    config = {}
  ): Promise<AxiosResponse> => API.request('patch', url, body, config);
  static put = (
    url: string,
    body: object,
    config = {}
  ): Promise<AxiosResponse> => API.request('put', url, body, config);
}

export default API;
